/**
 * Confirm Context
 *
 * Provider e hooks `useConfirm` / `usePrompt` para substituir
 * `window.confirm()` e `window.prompt()` nativos por dialog ergonômico
 * baseado em AlertDialog (Radix + shadcn).
 *
 * @since 2026-04-27
 * @issue TMF9-53670, TMF9-53764, TMF9-53766
 *
 * @example
 * const confirm = useConfirm();
 *
 * async function handleDelete() {
 *   const ok = await confirm({
 *     title: 'Excluir item?',
 *     description: 'Esta ação não pode ser desfeita.',
 *     variant: 'destructive',
 *   });
 *   if (ok) {
 *     await api.delete(...);
 *   }
 * }
 *
 * @example
 * const prompt = usePrompt();
 *
 * async function handleSetLink() {
 *   const url = await prompt({
 *     title: 'URL do link',
 *     defaultValue: 'https://',
 *     placeholder: 'https://exemplo.com',
 *   });
 *   if (url !== null) {
 *     editor.chain().focus().setLink({ href: url }).run();
 *   }
 * }
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/ConfirmDialog';
import { cn } from '@/lib/utils';

// ============================================================
// Types
// ============================================================

export type ConfirmVariant = 'default' | 'destructive';

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

export interface PromptOptions {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  inputType?: 'text' | 'url' | 'email' | 'number';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
type PromptFn = (options: PromptOptions) => Promise<string | null>;

interface ConfirmContextValue {
  confirm: ConfirmFn;
  prompt: PromptFn;
}

type PendingItem =
  | { kind: 'confirm'; options: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; options: PromptOptions; resolve: (v: string | null) => void };

// ============================================================
// Context
// ============================================================

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

// ============================================================
// Provider
// ============================================================

interface ConfirmProviderProps {
  children: ReactNode;
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [pending, setPending] = useState<PendingItem | null>(null);
  const [inputValue, setInputValue] = useState('');
  const queueRef = useRef<PendingItem[]>([]);

  useEffect(() => {
    if (pending?.kind === 'prompt') {
      setInputValue(pending.options.defaultValue ?? '');
    }
  }, [pending]);

  const flushNext = useCallback(() => {
    const next = queueRef.current.shift();
    setPending(next ?? null);
  }, []);

  const enqueue = useCallback((item: PendingItem) => {
    setPending((current) => {
      if (current) {
        queueRef.current.push(item);
        return current;
      }
      return item;
    });
  }, []);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        enqueue({ kind: 'confirm', options, resolve });
      }),
    [enqueue],
  );

  const prompt = useCallback<PromptFn>(
    (options) =>
      new Promise<string | null>((resolve) => {
        enqueue({ kind: 'prompt', options, resolve });
      }),
    [enqueue],
  );

  const resolveConfirm = useCallback(
    (value: boolean) => {
      if (pending?.kind === 'confirm') {
        pending.resolve(value);
        flushNext();
      }
    },
    [pending, flushNext],
  );

  const resolvePrompt = useCallback(
    (value: string | null) => {
      if (pending?.kind === 'prompt') {
        pending.resolve(value);
        flushNext();
      }
    },
    [pending, flushNext],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        if (pending?.kind === 'confirm') resolveConfirm(false);
        if (pending?.kind === 'prompt') resolvePrompt(null);
      }
    },
    [pending, resolveConfirm, resolvePrompt],
  );

  const isConfirm = pending?.kind === 'confirm';
  const isPrompt = pending?.kind === 'prompt';
  const opts = pending?.options;
  const variant = isConfirm
    ? (pending.options.variant ?? 'default')
    : 'default';
  const confirmText = opts?.confirmText ?? (isPrompt ? 'OK' : 'Confirmar');
  const cancelText = opts?.cancelText ?? 'Cancelar';

  const handlePromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    resolvePrompt(inputValue);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{opts?.title ?? ''}</AlertDialogTitle>
            {opts?.description && (
              <AlertDialogDescription>
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {isPrompt && (
            <form onSubmit={handlePromptSubmit}>
              <input
                type={pending.options.inputType ?? 'text'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={pending.options.placeholder}
                autoFocus
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </form>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                isConfirm ? resolveConfirm(false) : resolvePrompt(null)
              }
            >
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                isConfirm ? resolveConfirm(true) : resolvePrompt(inputValue)
              }
              className={cn(
                variant === 'default' &&
                  'bg-primary text-primary-foreground hover:bg-primary/90',
              )}
            >
              {confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

// ============================================================
// Hooks
// ============================================================

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm deve ser usado dentro de <ConfirmProvider>');
  }
  return ctx.confirm;
}

export function usePrompt(): PromptFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('usePrompt deve ser usado dentro de <ConfirmProvider>');
  }
  return ctx.prompt;
}
